import React from "react";

import css from "./form.module.scss";
import { Button } from "@mui/material";

export interface Props {
  key?: string;
  label?: string;
  disabled?: boolean;
  onSubmit: (event: any) => void;
  variant?: "contained" | "text" | "outlined" | undefined;
  inline?: boolean;
  color?:
    | "primary"
    | "inherit"
    | "secondary"
    | "success"
    | "error"
    | "info"
    | "warning"
    | "darkerGreen"
    | "white"
    | "resetDark";
  size?: "medium" | "small" | "large";
  startIcon?: any;
  endIcon?: any;
  dontTransformToUppercase?: boolean;
  /** Height in pixels for the button */
  heightPx?: number;
  marginTopPx?: number;
}

const FormButton: React.FC<Props> = (props) => {
  const {
    key,
    label,
    disabled,
    onSubmit,
    variant = "contained",
    inline = false,
    color = "primary",
    size = "medium",
    startIcon = undefined,
    endIcon = undefined,
    dontTransformToUppercase = false,
    heightPx,
    marginTopPx,
  } = props;

  /////////////////////////////////////////////////////////////////////////

  const isCustomColor =
    color === "darkerGreen" || color === "white" || color === "resetDark";
  const muiColor = isCustomColor ? undefined : color;

  const baseSx: any = {};
  if (dontTransformToUppercase) baseSx.textTransform = "none";
  if (typeof heightPx === "number") baseSx.height = `${heightPx}px`;
  if (typeof marginTopPx === "number") baseSx.marginTop = `${marginTopPx}px`;

  let sx = undefined;
  if (color === "resetDark") {
    sx = {
      ...baseSx,
      backgroundColor: "#132829",
      color: "#fff",
      ...(baseSx.height ? {} : { height: "40px" }),
      "&:hover": {
        backgroundColor: "#0e1d1f",
      },
      "&.Mui-disabled": {
        backgroundColor: "#b0b8bb",
        color: "#e0e0e0",
        opacity: 0.5,
        filter: "grayscale(60%)",
        cursor: "not-allowed",
        boxShadow: "none",
      },
    };
  } else if (color === "darkerGreen") {
    sx = {
      ...baseSx,
      backgroundColor: "#29666F",
      color: "white",
      ...(baseSx.height ? {} : { height: "40px" }),
      "&:hover": {
        backgroundColor: "#1f4d54",
      },
    };
  } else if (color === "white") {
    sx = {
      ...baseSx,
      backgroundColor: "#FFFFFF",
      color: "#1F2937", // dark text for readability
      border: "1px solid #E0E0E0",
      "&:hover": {
        backgroundColor: "#F5F5F5",
      },
    };
  } else if (Object.keys(baseSx).length > 0) {
    sx = baseSx;
  }

  return (
    <Button
      key={key}
      fullWidth={!inline}
      type="submit"
      className={inline ? css.buttonInline : css.button}
      variant={variant}
      color={muiColor}
      size={size}
      onClick={onSubmit}
      disabled={!!disabled}
      startIcon={startIcon}
      endIcon={endIcon}
      sx={sx}
    >
      {label}
    </Button>
  );
};

export default FormButton;
